import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Globe, Smartphone, Monitor, Tablet, Link2,
  TrendingUp, Eye, Users, BarChart3, Circle,
  FileText, Layers, Tag
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import { supabase } from "@/integrations/supabase/client";

const formatNumber = (n: number) => n.toLocaleString("pt-BR");

const COLORS = [
  "hsl(24, 95%, 53%)", "hsl(200, 80%, 50%)", "hsl(142, 71%, 45%)",
  "hsl(38, 92%, 50%)", "hsl(280, 60%, 50%)", "hsl(0, 72%, 51%)",
  "hsl(180, 60%, 45%)", "hsl(320, 70%, 55%)"
];

const PERIOD_OPTIONS = [
  { label: "7 dias", days: 7 },
  { label: "14 dias", days: 14 },
  { label: "30 dias", days: 30 },
];

const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "12px",
  fontSize: "11px",
  color: "hsl(var(--foreground))",
  boxShadow: "0 8px 32px -8px hsl(var(--foreground) / 0.1)",
  padding: "8px 12px",
};

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } };
const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } } };

const AdminTrafficTab = () => {
  const [visits, setVisits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7);

  const fetchVisits = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    const since = new Date();
    since.setDate(since.getDate() - period);
    const { data } = await supabase
      .from("page_visits")
      .select("*")
      .gte("created_at", since.toISOString())
      .order("created_at", { ascending: false })
      .limit(5000) as any;
    setVisits(data || []);
    if (showLoading) setLoading(false);
  };

  useEffect(() => {
    fetchVisits();
    const interval = setInterval(() => fetchVisits(false), 60_000);
    return () => clearInterval(interval);
  }, [period]);

  const realtimeCount = useMemo(() => {
    const fiveMinAgo = new Date(Date.now() - 5 * 60_000).toISOString();
    return visits.filter(v => v.created_at >= fiveMinAgo).length;
  }, [visits]);

  const uniqueSessions = useMemo(() => {
    const set = new Set(visits.map(v => v.session_id).filter(Boolean));
    return set.size;
  }, [visits]);

  const dailyData = useMemo(() => {
    const days: { date: string; label: string; views: number; sessions: number }[] = [];
    for (let i = period - 1; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      const dayVisits = visits.filter(v => v.created_at.slice(0, 10) === iso);
      const sessionsSet = new Set(dayVisits.map(v => v.session_id).filter(Boolean));
      days.push({ date: iso, label: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }), views: dayVisits.length, sessions: sessionsSet.size });
    }
    return days;
  }, [visits, period]);

  const topReferrers = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => { const domain = v.referrer_domain || "Direto"; map[domain] = (map[domain] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, value]) => ({ name, value }));
  }, [visits]);

  const utmSources = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => { if (v.utm_source) map[v.utm_source] = (map[v.utm_source] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, value]) => ({ name, value }));
  }, [visits]);

  const utmCampaigns = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => { if (v.utm_campaign) map[v.utm_campaign] = (map[v.utm_campaign] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([name, value]) => ({ name, value }));
  }, [visits]);

  const devices = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => { const d = v.device_type || "desktop"; map[d] = (map[d] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [visits]);

  const browsers = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => { const b = v.browser || "Outro"; map[b] = (map[b] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([name, value]) => ({ name, value }));
  }, [visits]);

  const osList = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => { const o = v.os || "Outro"; map[o] = (map[o] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([name, value]) => ({ name, value }));
  }, [visits]);

  const topPages = useMemo(() => {
    const map: Record<string, number> = {};
    visits.forEach(v => { map[v.page_path] = (map[v.page_path] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([path, count]) => ({ path, count }));
  }, [visits]);

  const DEVICE_ICONS: Record<string, any> = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };
  const DEVICE_LABELS: Record<string, string> = { desktop: "Desktop", mobile: "Mobile", tablet: "Tablet" };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const heroStats = [
    { label: "Page Views", value: formatNumber(visits.length), icon: Eye, sub: `últimos ${period} dias`, color: "from-orange-500/20 to-amber-500/10", iconBg: "bg-orange-500/15 text-orange-500" },
    { label: "Sessões Únicas", value: formatNumber(uniqueSessions), icon: Users, sub: "visitantes distintos", color: "from-blue-500/20 to-cyan-500/10", iconBg: "bg-blue-500/15 text-blue-500" },
    { label: "Fontes UTM", value: formatNumber(utmSources.length), icon: Link2, sub: "sources rastreadas", color: "from-emerald-500/20 to-green-500/10", iconBg: "bg-emerald-500/15 text-emerald-500" },
    { label: "Campanhas", value: formatNumber(utmCampaigns.length), icon: Tag, sub: "UTM campaigns", color: "from-violet-500/20 to-purple-500/10", iconBg: "bg-violet-500/15 text-violet-500" },
  ];

  return (
    <motion.div className="space-y-6" variants={stagger} initial="hidden" animate="visible">

      {/* ─── Header ─── */}
      <motion.div variants={fadeUp} className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-lg">Tráfego & Aquisição</h2>
          <p className="text-xs text-muted-foreground mt-0.5">De onde vêm seus acessos e novos usuários</p>
        </div>
        <div className="flex gap-1 bg-secondary/50 rounded-lg p-0.5">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.days}
              onClick={() => setPeriod(opt.days)}
              className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all duration-200 ${
                period === opt.days
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* ─── Real-time pulse ─── */}
      <motion.div variants={fadeUp} className="flex items-center gap-4 px-5 py-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04]">
        <div className="relative">
          <span className="absolute inset-0 animate-ping rounded-full bg-emerald-500/40" />
          <Circle className="relative h-4 w-4 fill-emerald-500 text-emerald-500" />
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-display font-bold">{realtimeCount}</span>
          <span className="text-xs text-muted-foreground">visitantes nos últimos 5 minutos</span>
        </div>
        <span className="text-[10px] text-muted-foreground/60 ml-auto">Atualiza a cada 1 min</span>
      </motion.div>

      {/* ─── Hero Stats ─── */}
      <motion.div variants={fadeUp} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {heroStats.map((s) => (
          <div
            key={s.label}
            className={`relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br ${s.color} p-5 group hover:border-primary/20 transition-all duration-300`}
          >
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-foreground/[0.02] group-hover:scale-125 transition-transform duration-500" />
            <div className={`w-10 h-10 rounded-xl ${s.iconBg} flex items-center justify-center mb-4`}>
              <s.icon className="h-5 w-5" />
            </div>
            <p className="text-2xl font-bold font-display leading-none tracking-tight">{s.value}</p>
            <p className="text-xs text-muted-foreground mt-1.5 font-medium">{s.label}</p>
            <p className="text-[11px] text-muted-foreground/70 mt-0.5">{s.sub}</p>
          </div>
        ))}
      </motion.div>

      {/* ─── Daily views chart ─── */}
      <motion.div variants={fadeUp} className="rounded-2xl border border-border/60 bg-card p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-display font-bold text-sm">Visitas Diárias</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">Page views e sessões únicas</p>
          </div>
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <BarChart3 className="h-4 w-4 text-primary" />
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={dailyData}>
            <defs>
              <linearGradient id="viewsGrad2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0.25} />
                <stop offset="100%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="sessionsGrad2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(200, 80%, 50%)" stopOpacity={0.15} />
                <stop offset="100%" stopColor="hsl(200, 80%, 50%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} dy={8} />
            <YAxis hide />
            <Tooltip contentStyle={tooltipStyle} />
            <Area type="monotone" dataKey="views" name="Views" stroke="hsl(24, 95%, 53%)" strokeWidth={2.5} fill="url(#viewsGrad2)" dot={{ r: 3, fill: "hsl(24, 95%, 53%)", strokeWidth: 0 }} activeDot={{ r: 5, strokeWidth: 2, stroke: "hsl(var(--background))" }} />
            <Area type="monotone" dataKey="sessions" name="Sessões" stroke="hsl(200, 80%, 50%)" strokeWidth={2} fill="url(#sessionsGrad2)" dot={{ r: 2, fill: "hsl(200, 80%, 50%)", strokeWidth: 0 }} />
          </AreaChart>
        </ResponsiveContainer>
        <div className="flex items-center justify-center gap-6 mt-3">
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground font-medium">
            <span className="w-3 h-[3px] rounded-full bg-[hsl(24,95%,53%)]" /> Page Views
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground font-medium">
            <span className="w-3 h-[3px] rounded-full bg-[hsl(200,80%,50%)]" /> Sessões Únicas
          </div>
        </div>
      </motion.div>

      {/* ─── Referrers + Devices ─── */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Referrers */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Globe className="h-4 w-4 text-blue-500" />
              </div>
              <div>
                <h3 className="font-display font-bold text-sm">Origem dos Acessos</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">Principais referenciadores</p>
              </div>
            </div>
          </div>
          {topReferrers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
              <Globe className="h-8 w-8 mb-2 opacity-15" />
              <p className="text-xs">Sem dados de referência</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {topReferrers.map((ref, i) => {
                const maxVal = topReferrers[0]?.value || 1;
                const pct = (ref.value / maxVal) * 100;
                return (
                  <div key={ref.name} className="relative group">
                    <div className="flex items-center justify-between relative z-10 px-3 py-2 rounded-lg">
                      <div className="flex items-center gap-2.5">
                        <span className="text-[10px] text-muted-foreground/50 w-4 font-mono">{i + 1}</span>
                        <span className="text-xs font-semibold truncate max-w-[180px] group-hover:text-primary transition-colors">{ref.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold tabular-nums">{formatNumber(ref.value)}</span>
                        <Badge variant="outline" className="text-[9px] border-border/50 tabular-nums">
                          {((ref.value / visits.length) * 100).toFixed(1)}%
                        </Badge>
                      </div>
                    </div>
                    <div className="absolute inset-y-0 left-0 rounded-lg bg-primary/[0.06]" style={{ width: `${pct}%` }} />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Devices */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-violet-500/10 flex items-center justify-center">
                <Layers className="h-4 w-4 text-violet-500" />
              </div>
              <div>
                <h3 className="font-display font-bold text-sm">Dispositivos</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">Desktop vs Mobile vs Tablet</p>
              </div>
            </div>
          </div>
          {devices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
              <Monitor className="h-8 w-8 mb-2 opacity-15" />
              <p className="text-xs">Sem dados</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={devices} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={4} dataKey="value" strokeWidth={0}>
                    {devices.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number, name: string) => [formatNumber(v), DEVICE_LABELS[name] || name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex gap-6 mt-3">
                {devices.map((d, idx) => {
                  const Icon = DEVICE_ICONS[d.name] || Monitor;
                  const pct = visits.length > 0 ? ((d.value / visits.length) * 100).toFixed(1) : "0";
                  return (
                    <div key={d.name} className="flex items-center gap-2 text-xs">
                      <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground font-medium">{DEVICE_LABELS[d.name] || d.name}</span>
                      <span className="font-bold tabular-nums">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* ─── UTM Sources + Browsers + OS ─── */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* UTM Sources */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-amber-500" />
            </div>
            <div>
              <h3 className="font-display font-bold text-sm">UTM Sources</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">Campanhas rastreadas</p>
            </div>
          </div>
          {utmSources.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[160px] text-muted-foreground">
              <Link2 className="h-8 w-8 mb-2 opacity-15" />
              <p className="text-xs">Nenhum UTM rastreado</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={utmSources} layout="vertical">
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={80} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [formatNumber(v), "Visitas"]} />
                <Bar dataKey="value" fill="hsl(24, 95%, 53%)" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Browsers */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-cyan-500/10 flex items-center justify-center">
              <Globe className="h-4 w-4 text-cyan-500" />
            </div>
            <div>
              <h3 className="font-display font-bold text-sm">Navegadores</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">Distribuição por browser</p>
            </div>
          </div>
          {browsers.length === 0 ? (
            <div className="flex items-center justify-center h-[160px] text-muted-foreground text-xs">Sem dados</div>
          ) : (
            <div className="space-y-2.5">
              {browsers.map((b, i) => {
                const pct = visits.length > 0 ? ((b.value / visits.length) * 100).toFixed(1) : "0";
                const maxVal = browsers[0]?.value || 1;
                const barPct = (b.value / maxVal) * 100;
                return (
                  <div key={b.name} className="relative group">
                    <div className="flex items-center gap-2.5 relative z-10 px-2.5 py-1.5 rounded-lg">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-xs flex-1 truncate font-medium group-hover:text-primary transition-colors">{b.name}</span>
                      <span className="text-xs font-bold tabular-nums">{pct}%</span>
                      <span className="text-[10px] text-muted-foreground/60 tabular-nums">({formatNumber(b.value)})</span>
                    </div>
                    <div className="absolute inset-y-0 left-0 rounded-lg bg-primary/[0.04]" style={{ width: `${barPct}%` }} />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* OS */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Monitor className="h-4 w-4 text-emerald-500" />
            </div>
            <div>
              <h3 className="font-display font-bold text-sm">Sistemas Operacionais</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">Distribuição por OS</p>
            </div>
          </div>
          {osList.length === 0 ? (
            <div className="flex items-center justify-center h-[160px] text-muted-foreground text-xs">Sem dados</div>
          ) : (
            <div className="space-y-2.5">
              {osList.map((o, i) => {
                const pct = visits.length > 0 ? ((o.value / visits.length) * 100).toFixed(1) : "0";
                const maxVal = osList[0]?.value || 1;
                const barPct = (o.value / maxVal) * 100;
                return (
                  <div key={o.name} className="relative group">
                    <div className="flex items-center gap-2.5 relative z-10 px-2.5 py-1.5 rounded-lg">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-xs flex-1 truncate font-medium group-hover:text-primary transition-colors">{o.name}</span>
                      <span className="text-xs font-bold tabular-nums">{pct}%</span>
                      <span className="text-[10px] text-muted-foreground/60 tabular-nums">({formatNumber(o.value)})</span>
                    </div>
                    <div className="absolute inset-y-0 left-0 rounded-lg bg-primary/[0.04]" style={{ width: `${barPct}%` }} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>

      {/* ─── Top Pages ─── */}
      <motion.div variants={fadeUp} className="rounded-2xl border border-border/60 bg-card overflow-hidden">
        <div className="p-5 border-b border-border/60 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
            <FileText className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h3 className="font-display font-bold text-sm">Páginas Mais Visitadas</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">Top 10 rotas acessadas</p>
          </div>
        </div>
        {topPages.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-xs">Sem dados</div>
        ) : (
          <div className="divide-y divide-border/30">
            {topPages.map((p, i) => {
              const maxVal = topPages[0]?.count || 1;
              const barPct = (p.count / maxVal) * 100;
              return (
                <div key={p.path} className="relative group">
                  <div className="flex items-center justify-between px-5 py-3.5 relative z-10 hover:bg-secondary/10 transition-colors">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className="text-[11px] text-muted-foreground/50 w-5 text-right font-mono tabular-nums">{i + 1}</span>
                      <span className="text-xs font-mono truncate group-hover:text-primary transition-colors">{p.path}</span>
                    </div>
                    <div className="flex items-center gap-2.5 shrink-0 ml-3">
                      <span className="text-xs font-bold tabular-nums">{formatNumber(p.count)}</span>
                      <Badge variant="outline" className="text-[9px] border-border/50 tabular-nums">
                        {((p.count / visits.length) * 100).toFixed(1)}%
                      </Badge>
                    </div>
                  </div>
                  <div className="absolute inset-y-0 left-0 bg-primary/[0.03]" style={{ width: `${barPct}%` }} />
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default AdminTrafficTab;
